import { isNotNull } from "option-t/nullable";
import type { ConditionalKeys } from "type-fest";

import { combineNullables } from "../util/combineNullables.js";
import type { BigFraction } from "./BigFraction.js";
import { FRACTION_ZERO } from "./fraction.js";

const sumReducer = (acc: BigFraction, curr: BigFraction) => acc.add(curr);
const sumNumberReducer = (acc: number, curr: number) => acc + curr;

/**
 * Sums given fractions. A null value is effectively interpreted as 0
 */
export const sum = (fractions: Array<BigFraction | null>): BigFraction | null =>
  combineNullables(
    (..._fractions) => _fractions.reduce(sumReducer, FRACTION_ZERO),
    ...fractions.filter(isNotNull),
  );

export const sumNumbers = (fractions: Array<number | null>): number | null =>
  combineNullables(
    (..._fractions) => _fractions.reduce(sumNumberReducer, 0),
    ...fractions.filter(isNotNull),
  );

export const sumKey = <
  TData extends ReadonlyArray<unknown>,
  TKey extends ConditionalKeys<TData[number], BigFraction | null>,
>(
  data: TData,
  key: TKey,
): BigFraction | null =>
  sum(
    (data as ReadonlyArray<Record<TKey, BigFraction | null>>).map(
      (obj) => obj[key],
    ),
  );

export const sumKeyNumbers = <
  TData extends ReadonlyArray<unknown>,
  TKey extends ConditionalKeys<TData[number], number | null>,
>(
  data: TData,
  key: TKey,
): number | null =>
  sumNumbers(
    (data as ReadonlyArray<Record<TKey, number | null>>).map((obj) => obj[key]),
  );
